// src/main/PaymentReceipt.js (Fixed Download - No Print/Preview)

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Grid,
    Slide,
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Divider,
    useTheme,
    Menu,
    Tooltip,
    Avatar,
    Alert,
    Snackbar,
    CircularProgress
} from '@mui/material';
import {
    Search,
    Add as AddIcon,
    Filter as FilterIcon,
    Refresh as RefreshIcon,
    Visibility as ViewIcon,
    PictureAsPdf,
    TableChart,
    GetApp,
    Print,
    CheckCircle,
    AttachMoney,
    Download,
    Receipt as ReceiptIcon,
    ReceiptLong,
    Pending
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APIPaymentList,
    APIPaymentReceiptList,
    APIPaymentReceiptStore,
    APIPaymentReceiptDownload,
    APIPaymentReceiptExportExcel,
    APIPaymentReceiptExportPDF,
    APIPaymentReceiptStats
} from '../../apiServices/APIs';
import { formatCreatedAtDate, recordsCount, recordsPerPage } from '../../utils/helpers';
import { getUserDetails } from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import ErrorState from '../../components/ErrorrState';
import LoadingState from '../../components/LoadingState';
import { usePermission } from '../../hooks/usePermission';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const PaymentReceipt = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [pageState, setPageState] = useState({
        receipts: [],
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statistics, setStatistics] = useState(null);
    const [payments, setPayments] = useState([]);
    const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState('');
    const [generating, setGenerating] = useState(false);
    const [downloadingId, setDownloadingId] = useState(null);

    const isMounted = useRef(true);

    const receiptStatusOptions = [
        { id: 'generated', name: 'Generated', color: 'info', icon: <ReceiptIcon /> },
    ];

    const [filters, setFilters] = useState({
        receipt_status: '',
        date_from: '',
        date_to: '',
    });

    const [filterOptions, setFilterOptions] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    const { hasPermission, loading: permissionLoading } = usePermission();

    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        fetchReceipts();
        fetchStatistics();

        return () => {
            isMounted.current = false;
        };
    }, [showToast]);

    // Fetch payments without receipts
    const fetchPayments = async () => {
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.list(userBranchId, APIPaymentList, { has_no_receipt: true });
            if (result.success && isMounted.current) {
                setPayments(Array.isArray(result.data) ? result.data : []);
            }
        } catch (error) {
            console.error("Error fetching payments:", error);
            if (isMounted.current) setPayments([]);
        }
    };

    // Fetch receipts
    const fetchReceipts = async () => {
        setLoading(true);
        setError(null);
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.list(userBranchId, APIPaymentReceiptList);
            if (result.success && isMounted.current) {
                const receipts = Array.isArray(result.data) ? result.data.reverse() : [];
                setPageState(prev => ({
                    ...prev,
                    receipts: receipts
                }));
            } else if (isMounted.current) {
                setError(result.message);
            }
        } catch (error) {
            if (isMounted.current) {
                setError("Failed to load data");
            }
            console.error("Fetch error:", error);
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    // Fetch statistics
    const fetchStatistics = async () => {
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.listStats(userBranchId, APIPaymentReceiptStats);
            if (result.success && result.data && isMounted.current) {
                setStatistics(result.data);
            }
        } catch (error) {
            console.error("Error fetching statistics:", error);
        }
    };

    // Generate receipt (save data only, no PDF)
    const handleGenerateReceipt = async () => {
        if (!hasPermission('ADD')) {
            showToast("Permission Denied", "You don't have permission to generate receipts", "error");
            return;
        }

        if (!selectedPayment) {
            showToast("Validation Error", "Please select a payment", "warning");
            return;
        }

        setGenerating(true);
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('user_id', getUserDetails("Id"));
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('payment_id', selectedPayment);

            const result = await apiService.create(APIPaymentReceiptStore, formDataToSend);
            if (result && result.success && isMounted.current) {
                showToast("Success", "Receipt record created successfully", "success");
                setGenerateDialogOpen(false);
                setSelectedPayment('');
                fetchReceipts();
                fetchStatistics();
                fetchPayments();
            } else if (isMounted.current) {
                showToast("Error", result.message || "Failed to create receipt", "error");
            }
        } catch (error) {
            console.error("Generate receipt error:", error);
            if (isMounted.current) {
                showToast("Error", error.message || "Failed to create receipt", "error");
            }
        } finally {
            if (isMounted.current) {
                setGenerating(false);
            }
        }
    };

    // Download receipt (fetches PDF as blob and triggers download)
// Download receipt using apiService download method
const handleDownloadReceipt = async (record) => {
    if (!hasPermission('VIEW')) {
        showToast("Permission Denied", "You don't have permission to download receipts", "error");
        return;
    }

    setDownloadingId(record.id);
    try {
        const formDataToSend = new FormData();
        formDataToSend.append('receipt_id', record.id);
        formDataToSend.append('branch_id', getUserDetails("branchId"));
        formDataToSend.append('user_name', getUserDetails("userName"));
        formDataToSend.append('user_id', getUserDetails("Id"));
        formDataToSend.append('user_id', getUserDetails("Id"));
        // Use the apiService download method
        const filename = `receipt_${record.receipt_number}.pdf`;
        const result = await apiService.download(APIPaymentReceiptDownload, formDataToSend, filename);
        
        if (result && result.success && isMounted.current) {
            showToast("Success", "Receipt downloaded successfully", "success");
        } else if (isMounted.current) {
            showToast("Error", result.message || "Failed to download receipt", "error");
        }
    } catch (error) {
        console.error("Download error:", error);
        if (isMounted.current) {
            showToast("Error", "Failed to download receipt", "error");
        }
    } finally {
        if (isMounted.current) {
            setDownloadingId(null);
        }
    }
};

    // Export handlers
    const handleExportClick = (event) => { setExportAnchorEl(event.currentTarget); };
    const handleExportClose = () => { setExportAnchorEl(null); };
    
    const handleExportExcel = async () => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to export receipts", "error");
            handleExportClose();
            return;
        }
        
        try {
            handleExportClose();
            showToast("Processing", "Preparing Excel export...", "info");
            const formDataToSend = new FormData();
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            const result = await apiService.exportToExcel(APIPaymentReceiptExportExcel, formDataToSend);
            if (result && result.success && isMounted.current) {
                showToast("Success", "Excel file downloaded successfully", "success");
            } else if (isMounted.current) {
                showToast("Error", result.message || "Failed to export Excel file", "error");
            }
        } catch (error) {
            console.error('Excel export error:', error);
            if (isMounted.current) {
                showToast("Error", error.message || "Failed to export Excel file", "error");
            }
        }
    };

    const handleExportPDF = async () => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to export receipts", "error");
            handleExportClose();
            return;
        }
        
        try {
            handleExportClose();
            showToast("Processing", "Preparing PDF export...", "info");

            const formDataToSend = new FormData();
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_name', getUserDetails("userName"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            const result = await apiService.exportToPDF(APIPaymentReceiptExportPDF, formDataToSend);

            if (result && result.success && isMounted.current) {
                showToast("Success", "PDF file downloaded successfully", "success");
            } else if (isMounted.current) {
                showToast("Error", result.message || "Failed to export PDF file", "error");
            }
        } catch (error) {
            console.error('PDF export error:', error);
            if (isMounted.current) {
                showToast("Error", error.message || "Failed to export PDF file", "error");
            }
        }
    };

    // Filter handlers
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const clearFilters = () => {
        setFilters({
            receipt_status: '',
            date_from: '',
            date_to: '',
        });
        setSearchTerm('');
        handleCloseOptions();
    };

    const applyFilters = () => {
        handleCloseOptions();
    };

    const handlefilterOptions = () => setFilterOptions(true);
    const handleCloseOptions = () => setFilterOptions(false);
    const handleCloseView = () => setOpenView(false);
    const handleOpenView = (record) => {
        setSelectedRecord(record);
        setOpenView(true);
    };

    // Filtered data with useMemo
    const filteredData = useMemo(() => {
        return pageState.receipts.filter(record => {
            const matchesSearch = searchTerm === '' ||
                record.tenant_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                record.receipt_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                record.payment_id?.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesStatus = !filters.receipt_status || 
                record.receipt_status === filters.receipt_status;

            let matchesDate = true;
            if (filters.date_from && record.generated_date) {
                matchesDate = matchesDate && record.generated_date >= filters.date_from;
            }
            if (filters.date_to && record.generated_date) {
                matchesDate = matchesDate && record.generated_date <= filters.date_to;
            }

            return matchesSearch && matchesStatus && matchesDate;
        });
    }, [pageState.receipts, searchTerm, filters]);

    // Pagination
    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const getStatusColor = (status) => {
        const map = { 'generated': 'info' };
        return map[status] || 'default';
    };

    const getStatusIcon = (status) => {
        const map = { 'generated': <ReceiptIcon /> };
        return map[status] || <Pending />;
    };

    const getStatusText = (status) => {
        const option = receiptStatusOptions.find(opt => opt.id === status);
        return option ? option.name : status;
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-UG', {
            style: 'currency',
            currency: 'UGX',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount || 0);
    };

    if (permissionLoading) {
        return <LoadingState message="Loading permissions..." variant="medical" size="large" fullPage />;
    }

    if (loading) {
        return <LoadingState message="Loading Receipt Data..." variant="medical" size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchReceipts} title="Unable to Load Receipt Data" variant="medical" fullPage retryText="Retry Loading" />;
    }

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Statistics Cards */}
            {statistics && (
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={4}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h6" color="text.secondary">Total Receipts</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                        {statistics.total_receipts || 0}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.primary.light, width: 56, height: 56 }}>
                                    <ReceiptLong sx={{ fontSize: 32 }} />
                                </Avatar>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h6" color="text.secondary">Total Amount</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                                        {formatCurrency(statistics.total_amount || 0)}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.success.light, width: 56, height: 56 }}>
                                    <AttachMoney sx={{ fontSize: 32 }} />
                                </Avatar>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h6" color="text.secondary">This Month</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.info.main }}>
                                        {statistics.this_month_count || 0}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.info.light, width: 56, height: 56 }}>
                                    <CheckCircle sx={{ fontSize: 32 }} />
                                </Avatar>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {/* Action Buttons */}
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, flexShrink: 0 }}>
                        Payment Receipts ({recordsCount(pageState.receipts.length)} Total)
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                        {hasPermission('ADD') && (
                            <Button
                                variant="contained"
                                color="warning"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                    fetchPayments();
                                    setGenerateDialogOpen(true);
                                }}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                            >
                                Generate Receipt
                            </Button>
                        )}
                        {hasPermission('VIEW') && (
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<FilterIcon />}
                                onClick={handlefilterOptions}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                            >
                                View Options
                            </Button>
                        )}
                        {hasPermission('EXPORT') && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<GetApp />}
                                onClick={handleExportClick}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                            >
                                Export Data
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            color="info"
                            startIcon={<RefreshIcon />}
                            onClick={() => { fetchReceipts(); fetchStatistics(); }}
                            sx={{ textTransform: 'none', borderRadius: '8px' }}
                        >
                            Refresh
                        </Button>
                    </Box>

                    <Paper sx={{
                        width: '300px',
                        p: '2px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: 'auto',
                        borderRadius: '8px',
                        background: theme.palette.primary.light
                    }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search receipts..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <Search sx={{ mr: 1, color: 'white' }} />,
                                sx: {
                                    '& fieldset': { border: 'none' },
                                    fontSize: '0.875rem',
                                    color: 'white',
                                    '& input': { color: 'white', '&::placeholder': { color: 'rgba(255,255,255,0.7)', opacity: 1 } }
                                }
                            }}
                            size="small"
                        />
                    </Paper>
                </Box>
            </Paper>

            {/* Export Menu */}
            <Menu anchorEl={exportAnchorEl} open={Boolean(exportAnchorEl)} onClose={handleExportClose}>
                <MenuItem onClick={handleExportExcel} sx={{ gap: 1 }}><TableChart fontSize="small" color="success" /> Export to Excel (.xlsx)</MenuItem>
                <MenuItem onClick={handleExportPDF} sx={{ gap: 1 }}><PictureAsPdf fontSize="small" color="error" /> Export to PDF (.pdf)</MenuItem>
            </Menu>

            {/* Receipts Table */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: theme.palette.primary.main, '& th': { color: 'white', fontWeight: 'bold', fontSize: '0.95rem' } }}>
                                    <TableCell>#</TableCell>
                                    <TableCell>Receipt No.</TableCell>
                                    <TableCell>Payment ID</TableCell>
                                    <TableCell>Tenant</TableCell>
                                    <TableCell>Amount (UGX)</TableCell>
                                    <TableCell>Generated Date</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((record, index) => (
                                    <TableRow key={record.id || index} hover sx={{ '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover } }}>
                                        <TableCell><Chip label={page * rowsPerPage + index + 1} color="primary" size="small" /></TableCell>
                                        <TableCell><Chip label={record.receipt_number} color="info" size="small" variant="outlined" /></TableCell>
                                        <TableCell><Typography variant="body2">{record.payment_id}</Typography></TableCell>
                                        <TableCell>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{record.tenant_name}</Typography>
                                                <Typography variant="caption" color="text.secondary">{record.tenant_phone}</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                                                {formatCurrency(record.amount)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{new Date(record.generated_date).toLocaleString()}</TableCell>
                                        <TableCell>
                                            <Chip 
                                                icon={getStatusIcon(record.receipt_status)} 
                                                label={getStatusText(record.receipt_status)} 
                                                color={getStatusColor(record.receipt_status)} 
                                                size="small" 
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            {hasPermission('VIEW') && (
                                                <Tooltip title="View Details">
                                                    <IconButton onClick={() => handleOpenView(record)} color="primary">
                                                        <ViewIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            <Tooltip title="Download Receipt PDF">
                                                <IconButton 
                                                    onClick={() => handleDownloadReceipt(record)} 
                                                    color="success"
                                                    disabled={downloadingId === record.id}
                                                >
                                                    {downloadingId === record.id ? <CircularProgress size={20} /> : <Download />}
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination 
                        rowsPerPageOptions={[5, 10, 25, 50, 100]} 
                        component="div" 
                        count={filteredData.length} 
                        rowsPerPage={rowsPerPage} 
                        page={page} 
                        onPageChange={handleChangePage} 
                        onRowsPerPageChange={handleChangeRowsPerPage} 
                        sx={{ mt: 2 }} 
                    />
                    {filteredData.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
                            <ReceiptLong sx={{ fontSize: 60, mb: 2 }} />
                            <Typography variant="h6">No receipts found</Typography>
                            <Typography variant="body2">Try adjusting your search query</Typography>
                        </Box>
                    )}
                </Paper>
            </motion.div>

            {/* Filter Dialog */}
            <Dialog
                open={filterOptions}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                disableEscapeKeyDown
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseOptions();
                    }
                }}
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
                        <Button variant="outlined" color="primary" startIcon={<RefreshIcon />} onClick={clearFilters}>
                            Show All Receipts
                        </Button>
                        <Divider>OR</Divider>
                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>Filter Receipts</Typography>
                        <Box>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Receipt Status</InputLabel>
                                    <Select name="receipt_status" value={filters.receipt_status} onChange={handleFilterChange} label="Receipt Status">
                                        <MenuItem value="">All Status</MenuItem>
                                        {receiptStatusOptions.map(item => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid><br/>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Date From" name="date_from" type="date" value={filters.date_from} onChange={handleFilterChange} InputLabelProps={{ shrink: true }} />
                            </Grid><br/>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Date To" name="date_to" type="date" value={filters.date_to} onChange={handleFilterChange} InputLabelProps={{ shrink: true }} />
                            </Grid>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseOptions} variant="contained">Close</Button>
                    <Button onClick={applyFilters} color="primary" variant="contained">Apply Filters</Button>
                </DialogActions>
            </Dialog>

            {/* Generate Receipt Dialog */}
            <Dialog
                open={generateDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        setGenerateDialogOpen(false);
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Generate Receipt</DialogTitle>
                <DialogContent>
                    <Box sx={{ p: 2 }}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Select a payment to create a receipt record. PDF will be generated when you download.
                        </Alert>
                        <FormControl fullWidth>
                            <InputLabel>Select Payment</InputLabel>
                            <Select value={selectedPayment} onChange={(e) => setSelectedPayment(e.target.value)} label="Select Payment">
                                <MenuItem value="">Select a payment</MenuItem>
                                {payments.map(payment => (
                                    <MenuItem key={payment.id} value={payment.id}>
                                        {payment.payment_id} - {payment.tenant_name} - {formatCurrency(payment.amount)} 
                                        ({new Date(payment.payment_date).toLocaleDateString()})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {payments.length === 0 && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                No pending payments found. All payments already have receipts.
                            </Alert>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={() => setGenerateDialogOpen(false)} variant="contained">
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleGenerateReceipt} 
                        color="primary" 
                        variant="contained" 
                        disabled={generating || !selectedPayment}
                    >
                        {generating ? <CircularProgress size={24} /> : 'Create Receipt'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View Dialog */}
            <Dialog
                open={openView}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseView();
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ textAlign: 'center' }}><b>Receipt Details</b></DialogTitle>
                <DialogContent>
                    {selectedRecord && (
                        <Box sx={{ p: 2 }}>
                            <Box>
                                <Grid item xs={12}>
                                    <Typography variant="h6" color="primary">Receipt Information</Typography>
                                </Grid><br/>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Receipt Number:</b> {selectedRecord.receipt_number}</Typography>
                                </Grid><br/>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Receipt ID:</b> {selectedRecord.receipt_id}</Typography>
                                </Grid><br/>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Payment ID:</b> {selectedRecord.payment_id}</Typography>
                                </Grid><br/>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Generated Date:</b> {new Date(selectedRecord.generated_date).toLocaleString()}</Typography>
                                </Grid><br/>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2">
                                        <b>Status:</b>{' '}
                                        <Chip 
                                            icon={getStatusIcon(selectedRecord.receipt_status)} 
                                            label={getStatusText(selectedRecord.receipt_status)} 
                                            color={getStatusColor(selectedRecord.receipt_status)} 
                                            size="small" 
                                        />
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="h6" color="primary" sx={{ mt: 2 }}>Payment Information</Typography>
                                </Grid><br/>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Tenant:</b> {selectedRecord.tenant_name}</Typography>
                                </Grid><br/>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Amount:</b> {formatCurrency(selectedRecord.amount)}</Typography>
                                </Grid><br/>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Payment Method:</b> {selectedRecord.payment_method}</Typography>
                                </Grid><br/>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Payment Date:</b> {new Date(selectedRecord.payment_date).toLocaleString()}</Typography>
                                </Grid><br/>
                                {selectedRecord.transaction_reference && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2"><b>Transaction Reference:</b> {selectedRecord.transaction_reference}</Typography>
                                    </Grid>
                                )}<br/>
                                <Grid item xs={12}>
                                    <Typography variant="h6" color="primary" sx={{ mt: 2 }}>Property Information</Typography>
                                </Grid><br/>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Property:</b> {selectedRecord.property_name}</Typography>
                                </Grid><br/>
                                <Grid item xs={12}>
                                    <Typography variant="h6" color="primary" sx={{ mt: 2 }}>System Information</Typography>
                                </Grid><br/>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Created:</b> {formatCreatedAtDate(selectedRecord.created_at)}</Typography>
                                </Grid><br/>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Updated:</b> {formatCreatedAtDate(selectedRecord.updated_at)}</Typography>
                                </Grid><br/>
                            </Box>
                            <Box sx={{ mt: 3, textAlign: 'center' }}>
                                <Button 
                                    variant="outlined" 
                                    startIcon={<Download />} 
                                    onClick={() => {
                                        handleCloseView();
                                        handleDownloadReceipt(selectedRecord);
                                    }}
                                >
                                    Download PDF
                                </Button>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseView} variant="contained">Close</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default PaymentReceipt;

